/**
 * ============================================================================
 * STORAGE PAGE - 100% Ready
 * ============================================================================
 * 
 * Page for managing user file storage
 */

import React, { useEffect, useState } from 'react';
import { UserDashboardLayout } from '../components/user';
import { useStorage } from '../hooks/useStorage';
import { useToast } from '../providers';
import { useI18n } from '../providers';
import Card from '../components/common/Card';
import Tabs from '../components/common/Tabs';
import Spinner from '../components/common/Spinner';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import FileUpload from '../components/storage/FileUpload';
import FileManager from '../components/storage/FileManager';
import FileGallery from '../components/storage/FileGallery';
import StorageQuota from '../components/storage/StorageQuota';
import '../styles/animations.css';
import '../styles/global.css';

/**
 * Storage Page Component
 * @returns {JSX.Element}
 */
const Storage = () => {
  const { listFiles, getStorageQuota, loading } = useStorage();
  const { showError } = useToast();
  const { language } = useI18n();

  const [activeTab, setActiveTab] = useState('files');
  const [files, setFiles] = useState([]);
  const [quota, setQuota] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [filesData, quotaData] = await Promise.all([
          listFiles().catch(() => []),
          getStorageQuota().catch(() => ({ used: 0, limit: 10 * 1024 * 1024 * 1024 })),
        ]);
        setFiles(Array.isArray(filesData) ? filesData : filesData?.files || []);
        setQuota(quotaData);
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du chargement des fichiers' : 'Failed to load files')
        );
        console.error('Load storage error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [listFiles, getStorageQuota, showError, language]);

  const handleRefresh = async () => {
    try {
      setIsLoading(true);
      const [filesData, quotaData] = await Promise.all([
        listFiles().catch(() => []),
        getStorageQuota().catch(() => ({ used: 0, limit: 10 * 1024 * 1024 * 1024 })),
      ]);
      setFiles(Array.isArray(filesData) ? filesData : filesData?.files || []);
      setQuota(quotaData);
    } catch (error) {
      showError(
        error.message || (language === 'fr' ? 'Échec du rafraîchissement' : 'Failed to refresh')
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadComplete = async (uploadedFiles) => {
    // Refresh file list after upload
    try {
      const [filesData, quotaData] = await Promise.all([
        listFiles().catch(() => []),
        getStorageQuota().catch(() => ({ used: 0, limit: 10 * 1024 * 1024 * 1024 })),
      ]);
      setFiles(Array.isArray(filesData) ? filesData : filesData?.files || []);
      setQuota(quotaData);
    } catch (error) {
      console.error('Refresh after upload error:', error);
    }
  };

  const handleFileDelete = async () => {
    // Refresh file list after delete
    try {
      const [filesData, quotaData] = await Promise.all([
        listFiles().catch(() => []),
        getStorageQuota().catch(() => ({ used: 0, limit: 10 * 1024 * 1024 * 1024 })),
      ]);
      setFiles(Array.isArray(filesData) ? filesData : filesData?.files || []);
      setQuota(quotaData);
    } catch (error) {
      console.error('Refresh after delete error:', error);
    }
  };

  const tabs = [
    {
      id: 'files',
      label: language === 'fr' ? 'Fichiers' : 'Files',
      icon: '📁',
    },
    {
      id: 'upload',
      label: language === 'fr' ? 'Téléverser' : 'Upload',
      icon: '📤',
    },
    {
      id: 'gallery',
      label: language === 'fr' ? 'Galerie' : 'Gallery',
      icon: '🖼️',
    },
  ];

  if (isLoading && !files) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Spinner size="lg" />
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      {/* Header */}
      <div className="mb-8 animate-fadeInUp">
        <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
          💾 {language === 'fr' ? 'Mon Stockage' : 'My Storage'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          {language === 'fr'
            ? 'Gérez vos fichiers et votre espace de stockage'
            : 'Manage your files and storage space'}
        </p>
      </div>

      {/* File Organization - Search and Filters */}
      {activeTab === 'files' && (
        <Card
          variant="glass"
          className="mb-6 backdrop-blur-xl border-white/20 dark:border-white/10 animate-fadeInUp"
          style={{ animationDelay: '0.05s' }}
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={language === 'fr' ? 'Rechercher des fichiers...' : 'Search files...'}
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                options={[
                  { value: 'date', label: language === 'fr' ? 'Date (Récent)' : 'Date (Recent)' },
                  { value: 'name', label: language === 'fr' ? 'Nom (A-Z)' : 'Name (A-Z)' },
                  { value: 'size', label: language === 'fr' ? 'Taille' : 'Size' },
                  { value: 'type', label: language === 'fr' ? 'Type' : 'Type' },
                ]}
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
                options={[
                  { value: 'all', label: language === 'fr' ? 'Tous les fichiers' : 'All Files' },
                  { value: 'images', label: language === 'fr' ? 'Images' : 'Images' },
                  { value: 'documents', label: language === 'fr' ? 'Documents' : 'Documents' },
                  { value: 'videos', label: language === 'fr' ? 'Vidéos' : 'Videos' },
                ]}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Storage Quota */}
      <div className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
        <StorageQuota
          used={quota?.used || 0}
          limit={quota?.limit || 10 * 1024 * 1024 * 1024}
          onRefresh={handleRefresh}
        />
      </div>

      {/* Tabs */}
      <Card variant="glass" className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
        <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} variant="pills" />
      </Card>

      {/* Tab Content */}
      <div className="animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
        {activeTab === 'files' && (
          <FileManager
            files={
              (files || [])
                .filter((file) => {
                  // Search filter
                  if (searchQuery) {
                    const query = searchQuery.toLowerCase();
                    return (
                      file.name?.toLowerCase().includes(query) ||
                      file.type?.toLowerCase().includes(query)
                    );
                  }
                  return true;
                })
                .filter((file) => {
                  // Type filter
                  if (filterBy === 'all') return true;
                  if (filterBy === 'images') {
                    return file.type?.startsWith('image/');
                  }
                  if (filterBy === 'documents') {
                    return file.type?.includes('pdf') || file.type?.includes('document') || file.type?.includes('text');
                  }
                  if (filterBy === 'videos') {
                    return file.type?.startsWith('video/');
                  }
                  return true;
                })
                .sort((a, b) => {
                  // Sort filter
                  if (sortBy === 'date') {
                    return new Date(b.createdAt || b.uploadedAt || 0) - new Date(a.createdAt || a.uploadedAt || 0);
                  }
                  if (sortBy === 'name') {
                    return (a.name || '').localeCompare(b.name || '');
                  }
                  if (sortBy === 'size') {
                    return (b.size || 0) - (a.size || 0);
                  }
                  if (sortBy === 'type') {
                    return (a.type || '').localeCompare(b.type || '');
                  }
                  return 0;
                })
            }
            onFileDelete={handleFileDelete}
            onFileDownload={async () => {
              await listFiles();
            }}
          />
        )}

        {activeTab === 'upload' && (
          <Card variant="glass" className="animate-fadeInUp">
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <span className="text-2xl">📤</span>
                  {language === 'fr' ? 'Téléverser des fichiers' : 'Upload Files'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'fr'
                    ? 'Glissez-déposez vos fichiers ou cliquez pour sélectionner'
                    : 'Drag and drop your files or click to select'}
                </p>
              </div>
              <FileUpload
                onUploadComplete={handleUploadComplete}
                multiple
                maxFiles={10}
                maxSize={50 * 1024 * 1024} // 50MB
                folder="user-uploads"
              />
            </div>
          </Card>
        )}

        {activeTab === 'gallery' && (
          <FileGallery
            files={files || []}
            columns={4}
            onFileClick={(file) => {
              // Handle file click (e.g., open preview)
              console.log('File clicked:', file);
            }}
          />
        )}
      </div>
    </UserDashboardLayout>
  );
};

export default Storage;
